import { MemoryBus } from '../interfaces/memory-bus';
import { Logger } from '../util/logger';

const CPU_HZ = 1789773; // NTSC
const SAMPLE_RATE = 44100;
const CYCLES_PER_SAMPLE = CPU_HZ / SAMPLE_RATE; // ~40.5858 CPU cycles per sample

const LENGTH_TABLE = [
    10, 254, 20, 2, 40, 4, 80, 6, 160, 8, 60, 10, 14, 12, 26, 14,
    12, 16, 24, 18, 48, 20, 96, 22, 192, 24, 72, 26, 16, 28, 32, 30,
];

const DUTY_TABLE = [
    [0, 1, 0, 0, 0, 0, 0, 0], // 12.5%
    [0, 1, 1, 0, 0, 0, 0, 0], // 25%
    [0, 1, 1, 1, 1, 0, 0, 0], // 50%
    [1, 0, 0, 1, 1, 1, 1, 1], // 25% negated
];

const TRIANGLE_SEQ = [
    15, 14, 13, 12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 0,
    0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15,
];

// NTSC noise timer periods (APU cycles)
const NOISE_PERIODS = [4, 8, 16, 32, 64, 96, 128, 160, 202, 254, 380, 508, 762, 1016, 2034, 4068];

// NTSC DMC timer periods (CPU cycles per bit)
const DMC_PERIODS = [428, 380, 340, 320, 286, 254, 226, 214, 190, 160, 142, 128, 106, 84, 72, 54];

// Nonlinear mixer lookup for the two pulse channels (index = p1 + p2)
const PULSE_MIX = new Float32Array(31);
for (let i = 1; i < 31; i++) PULSE_MIX[i] = 95.88 / ((8128 / i) + 100);

/** Envelope generator shared by pulse and noise channels. */
class Envelope {
    constantVolume = false;
    loop = false; // also the length-counter halt flag
    private volume = 0; // constant volume / decay divider period
    private start = false;
    private divider = 0;
    private decay = 0;

    write(value: number): void {
        this.loop = (value & 0x20) !== 0;
        this.constantVolume = (value & 0x10) !== 0;
        this.volume = value & 0x0F;
    }

    restart(): void {
        this.start = true;
    }

    /** Quarter-frame clock. */
    clock(): void {
        if (this.start) {
            this.start = false;
            this.decay = 15;
            this.divider = this.volume;
        } else if (this.divider > 0) {
            this.divider--;
        } else {
            this.divider = this.volume;
            if (this.decay > 0) {
                this.decay--;
            } else if (this.loop) {
                this.decay = 15;
            }
        }
    }

    output(): number {
        return this.constantVolume ? this.volume : this.decay;
    }
}

/** Sweep unit for the pulse channels. */
class Sweep {
    private enabled = false;
    private period = 0;
    private negate = false;
    private shift = 0;
    private divider = 0;
    private reload = false;

    constructor(private readonly isPulse1: boolean) { }

    write(value: number): void {
        this.enabled = (value & 0x80) !== 0;
        this.period = (value >> 4) & 0x07;
        this.negate = (value & 0x08) !== 0;
        this.shift = value & 0x07;
        this.reload = true;
    }

    target(timer: number): number {
        const change = timer >> this.shift;
        if (this.negate) {
            // Pulse 1 uses one's complement (-change-1); pulse 2 two's complement.
            return timer - change - (this.isPulse1 ? 1 : 0);
        }
        return timer + change;
    }

    /** Half-frame clock. Returns the (possibly updated) channel timer period. */
    clock(timer: number): number {
        let result = timer;
        const target = this.target(timer);
        if (this.divider === 0 && this.enabled && this.shift > 0 && timer >= 8 && target <= 0x7FF) {
            result = target;
        }
        if (this.divider === 0 || this.reload) {
            this.divider = this.period;
            this.reload = false;
        } else {
            this.divider--;
        }
        return result;
    }

    /** Hardware mute condition (regardless of sweep enable). */
    muted(timer: number): boolean {
        return timer < 8 || this.target(timer) > 0x7FF;
    }
}

/** Pulse wave channel (1 or 2). */
class Pulse {
    enabled = false;
    lengthCounter = 0;
    timer = 0; // 11-bit period
    private duty = 0;
    private timerCount = 0;
    private seqPos = 0;
    private envelope = new Envelope();
    private sweep: Sweep;

    constructor(isPulse1: boolean) {
        this.sweep = new Sweep(isPulse1);
    }

    writeRegister(index: number, value: number): void {
        switch (index) {
            case 0:
                this.duty = (value >> 6) & 0x03;
                this.envelope.write(value);
                break;
            case 1:
                this.sweep.write(value);
                break;
            case 2:
                this.timer = (this.timer & 0x700) | value;
                break;
            case 3:
                this.timer = (this.timer & 0xFF) | ((value & 0x07) << 8);
                if (this.enabled) {
                    this.lengthCounter = LENGTH_TABLE[(value >> 3) & 0x1F];
                }
                this.seqPos = 0;
                this.envelope.restart();
                break;
        }
    }

    /** Clocked every second CPU cycle (APU cycle). */
    clockTimer(): void {
        if (this.timerCount === 0) {
            this.timerCount = this.timer;
            this.seqPos = (this.seqPos + 1) & 0x07;
        } else {
            this.timerCount--;
        }
    }

    /** Half-frame clock. */
    clockLengthAndSweep(): void {
        if (this.lengthCounter > 0 && !this.envelope.loop) {
            this.lengthCounter--;
        }
        this.timer = this.sweep.clock(this.timer);
    }

    clockEnvelope(): void {
        this.envelope.clock();
    }

    output(): number {
        if (!this.enabled || this.lengthCounter === 0) return 0;
        if (this.sweep.muted(this.timer)) return 0;
        if (DUTY_TABLE[this.duty][this.seqPos] === 0) return 0;
        return this.envelope.output();
    }
}

/** Triangle wave channel. */
class Triangle {
    enabled = false;
    lengthCounter = 0;
    timer = 0; // 11-bit period
    private control = false; // also length-counter halt
    private linearReloadValue = 0;
    private linearCounter = 0;
    private linearReloadFlag = false;
    private timerCount = 0;
    private seqPos = 0;

    writeRegister(index: number, value: number): void {
        switch (index) {
            case 0:
                this.control = (value & 0x80) !== 0;
                this.linearReloadValue = value & 0x7F;
                break;
            case 2:
                this.timer = (this.timer & 0x700) | value;
                break;
            case 3:
                this.timer = (this.timer & 0xFF) | ((value & 0x07) << 8);
                if (this.enabled) {
                    this.lengthCounter = LENGTH_TABLE[(value >> 3) & 0x1F];
                }
                this.linearReloadFlag = true;
                break;
        }
    }

    /** Clocked every CPU cycle. */
    clockTimer(): void {
        if (this.timerCount === 0) {
            this.timerCount = this.timer;
            if (this.enabled && this.lengthCounter > 0 && this.linearCounter > 0) {
                this.seqPos = (this.seqPos + 1) & 0x1F;
            }
        } else {
            this.timerCount--;
        }
    }

    /** Quarter-frame clock. */
    clockLinearCounter(): void {
        if (this.linearReloadFlag) {
            this.linearCounter = this.linearReloadValue;
        } else if (this.linearCounter > 0) {
            this.linearCounter--;
        }
        if (!this.control) {
            this.linearReloadFlag = false;
        }
    }

    /** Half-frame clock. */
    clockLengthCounter(): void {
        if (this.lengthCounter > 0 && !this.control) {
            this.lengthCounter--;
        }
    }

    output(): number {
        if (!this.enabled || this.lengthCounter === 0 || this.linearCounter === 0) return 0;
        if (this.timer < 2) return 0; // ultrasonic: silenced to avoid popping
        return TRIANGLE_SEQ[this.seqPos];
    }
}

/** Noise channel (15-bit LFSR). */
class Noise {
    enabled = false;
    lengthCounter = 0;
    private mode = false; // short LFSR (bit 6 tap) when true
    private timerPeriod = NOISE_PERIODS[0];
    private timerCount = 0;
    private lfsr = 1;
    private envelope = new Envelope();

    writeRegister(index: number, value: number): void {
        switch (index) {
            case 0:
                this.envelope.write(value);
                break;
            case 2:
                this.mode = (value & 0x80) !== 0;
                this.timerPeriod = NOISE_PERIODS[value & 0x0F];
                break;
            case 3:
                if (this.enabled) {
                    this.lengthCounter = LENGTH_TABLE[(value >> 3) & 0x1F];
                }
                this.envelope.restart();
                break;
        }
    }

    /** Clocked every second CPU cycle (APU cycle). */
    clockTimer(): void {
        if (this.timerCount === 0) {
            this.timerCount = this.timerPeriod;
            const tap = this.mode ? 6 : 1;
            const feedback = (this.lfsr & 0x01) ^ ((this.lfsr >> tap) & 0x01);
            this.lfsr = (this.lfsr >> 1) | (feedback << 14);
        } else {
            this.timerCount--;
        }
    }

    /** Half-frame clock. */
    clockLengthCounter(): void {
        if (this.lengthCounter > 0 && !this.envelope.loop) {
            this.lengthCounter--;
        }
    }

    clockEnvelope(): void {
        this.envelope.clock();
    }

    output(): number {
        if (!this.enabled || this.lengthCounter === 0) return 0;
        if ((this.lfsr & 0x01) !== 0) return 0;
        return this.envelope.output();
    }
}

/** Delta modulation channel (sample playback). */
class Dmc {
    irqFlag = false;
    bytesRemaining = 0;
    private irqEnabled = false;
    private loop = false;
    private timerPeriod = DMC_PERIODS[0];
    private timerCount = 0;
    private outputLevel = 0;
    private sampleAddress = 0xC000;
    private sampleLength = 1;
    private currentAddress = 0xC000;
    private sampleBuffer = -1; // -1 = empty
    private bitsRemaining = 8;

    constructor(private readonly bus: MemoryBus) { }

    writeRegister(index: number, value: number): void {
        switch (index) {
            case 0: // $4010
                this.irqEnabled = (value & 0x80) !== 0;
                this.loop = (value & 0x40) !== 0;
                this.timerPeriod = DMC_PERIODS[value & 0x0F];
                if (!this.irqEnabled) this.irqFlag = false;
                break;
            case 1: // $4011 direct load
                this.outputLevel = value & 0x7F;
                break;
            case 2: // $4012
                this.sampleAddress = 0xC000 + value * 0x40;
                break;
            case 3: // $4013
                this.sampleLength = value * 0x10 + 1;
                break;
        }
    }

    setEnabled(enabled: boolean): void {
        if (!enabled) {
            this.bytesRemaining = 0;
        } else if (this.bytesRemaining === 0) {
            this.restart();
        }
    }

    clearIrq(): void {
        this.irqFlag = false;
    }

    private restart(): void {
        this.currentAddress = this.sampleAddress;
        this.bytesRemaining = this.sampleLength;
    }

    /** Clocked every CPU cycle. */
    clockTimer(): void {
        if (this.sampleBuffer === -1 && this.bytesRemaining > 0) {
            this.fillBuffer();
        }
        if (this.sampleBuffer === -1) return;

        if (this.timerCount > 0) {
            this.timerCount--;
            return;
        }
        this.timerCount = this.timerPeriod - 1;

        if ((this.sampleBuffer & 0x01) !== 0) {
            if (this.outputLevel <= 125) this.outputLevel += 2;
        } else {
            if (this.outputLevel >= 2) this.outputLevel -= 2;
        }
        this.sampleBuffer >>= 1;
        if (--this.bitsRemaining === 0) {
            this.sampleBuffer = -1;
            this.bitsRemaining = 8;
        }
    }

    private fillBuffer(): void {
        this.sampleBuffer = this.bus.read(this.currentAddress);
        this.bitsRemaining = 8;
        // NOTE: the hardware stalls the CPU ~4 cycles here; not modeled (see report).
        this.currentAddress = this.currentAddress === 0xFFFF ? 0x8000 : this.currentAddress + 1;
        this.bytesRemaining--;
        if (this.bytesRemaining === 0) {
            if (this.loop) {
                this.restart();
            } else if (this.irqEnabled) {
                this.irqFlag = true;
            }
        }
    }

    output(): number {
        return this.outputLevel;
    }
}

/**
 * Ricoh 2A03 APU: two pulse channels, triangle, noise, DMC, frame counter,
 * status register, and nonlinear sample mixing.
 *
 * Contract (see AGENTS.md): constructor(bus), step(cpuCycles), read(addr),
 * write(addr, value), setIrqCallback(cb), drainSamples(). No browser APIs.
 */
export class Apu2A03 {
    private bus: MemoryBus;
    private logger = new Logger('APU', 0);

    private pulse1 = new Pulse(true);
    private pulse2 = new Pulse(false);
    private triangle = new Triangle();
    private noise = new Noise();
    private dmc: Dmc;

    // Frame counter ($4017)
    private fiveStepMode = false;
    private irqInhibit = false;
    private frameIrq = false;
    private frameCycle = 0; // CPU cycles within the current frame-counter sequence

    private oddCycle = false; // APU clock divider (APU cycles = CPU cycles / 2)

    // Sampling
    private sampleAcc = 0;
    private sampleQueue: number[] = [];
    private static readonly MAX_QUEUED_SAMPLES = 16384;

    private irqCallback: ((asserted: boolean) => void) | null = null;
    private irqLine = false;

    constructor(bus: MemoryBus) {
        this.bus = bus;
        this.dmc = new Dmc(bus);
        this.reset();
    }

    reset(): void {
        this.write(0x4015, 0x00); // disable all channels
        this.frameCycle = 0;
        this.frameIrq = false;
        this.fiveStepMode = false;
        this.irqInhibit = false;
        this.oddCycle = false;
        this.sampleAcc = 0;
        this.sampleQueue.length = 0;
        this.dmc.clearIrq();
        this.updateIrqLine();
    }

    setIrqCallback(cb: (asserted: boolean) => void): void {
        this.irqCallback = cb;
    }

    read(address: number): number {
        if (address === 0x4015) {
            const status =
                (this.pulse1.lengthCounter > 0 ? 0x01 : 0) |
                (this.pulse2.lengthCounter > 0 ? 0x02 : 0) |
                (this.triangle.lengthCounter > 0 ? 0x04 : 0) |
                (this.noise.lengthCounter > 0 ? 0x08 : 0) |
                (this.dmc.bytesRemaining > 0 ? 0x10 : 0) |
                (this.frameIrq ? 0x40 : 0) |
                (this.dmc.irqFlag ? 0x80 : 0);
            this.frameIrq = false; // reading $4015 clears the frame IRQ flag
            this.updateIrqLine();
            return status;
        }
        return 0; // all other APU registers are write-only
    }

    write(address: number, value: number): void {
        value &= 0xFF;
        switch (address) {
            case 0x4000: case 0x4001: case 0x4002: case 0x4003:
                this.pulse1.writeRegister(address - 0x4000, value);
                break;
            case 0x4004: case 0x4005: case 0x4006: case 0x4007:
                this.pulse2.writeRegister(address - 0x4004, value);
                break;
            case 0x4008: case 0x400A: case 0x400B:
                this.triangle.writeRegister(address === 0x4008 ? 0 : address === 0x400A ? 2 : 3, value);
                break;
            case 0x400C: case 0x400E: case 0x400F:
                this.noise.writeRegister(address === 0x400C ? 0 : address === 0x400E ? 2 : 3, value);
                break;
            case 0x4010: case 0x4011: case 0x4012: case 0x4013:
                this.dmc.writeRegister(address - 0x4010, value);
                this.updateIrqLine();
                break;
            case 0x4015: {
                this.pulse1.enabled = (value & 0x01) !== 0;
                this.pulse2.enabled = (value & 0x02) !== 0;
                this.triangle.enabled = (value & 0x04) !== 0;
                this.noise.enabled = (value & 0x08) !== 0;
                if (!this.pulse1.enabled) this.pulse1.lengthCounter = 0;
                if (!this.pulse2.enabled) this.pulse2.lengthCounter = 0;
                if (!this.triangle.enabled) this.triangle.lengthCounter = 0;
                if (!this.noise.enabled) this.noise.lengthCounter = 0;
                this.dmc.setEnabled((value & 0x10) !== 0);
                this.dmc.clearIrq(); // writing $4015 clears the DMC IRQ flag
                this.updateIrqLine();
                break;
            }
            case 0x4017: {
                this.fiveStepMode = (value & 0x80) !== 0;
                this.irqInhibit = (value & 0x40) !== 0;
                if (this.irqInhibit) {
                    this.frameIrq = false;
                }
                this.frameCycle = 0;
                if (this.fiveStepMode) {
                    this.clockQuarterFrame();
                    this.clockHalfFrame();
                }
                // NOTE: 3-4 CPU cycle delay before the reset takes effect on
                // hardware is not modeled (see report).
                this.updateIrqLine();
                break;
            }
        }
    }

    /** Advance the APU by the given number of CPU cycles. */
    step(cpuCycles: number): void {
        for (let i = 0; i < cpuCycles; i++) {
            this.oddCycle = !this.oddCycle;
            this.triangle.clockTimer();
            if (this.oddCycle) {
                this.pulse1.clockTimer();
                this.pulse2.clockTimer();
                this.noise.clockTimer();
            }
            this.dmc.clockTimer();
            this.clockFrameCounter();
            this.sampleCycle();
        }
    }

    /** Return and clear all pending mono samples (~44100 Hz, [-0.3, 1.0]). */
    drainSamples(): Float32Array {
        const out = new Float32Array(this.sampleQueue);
        this.sampleQueue.length = 0;
        return out;
    }

    // ---- internals ----

    private clockFrameCounter(): void {
        this.frameCycle++;
        if (!this.fiveStepMode) {
            // 4-step: quarter/half at CPU cycles 7457, 14913, 22371, 29829
            switch (this.frameCycle) {
                case 7457:
                    this.clockQuarterFrame();
                    break;
                case 14913:
                    this.clockQuarterFrame();
                    this.clockHalfFrame();
                    break;
                case 22371:
                    this.clockQuarterFrame();
                    break;
                case 29829:
                    this.clockQuarterFrame();
                    this.clockHalfFrame();
                    if (!this.irqInhibit) {
                        this.frameIrq = true;
                        this.updateIrqLine();
                    }
                    this.frameCycle = 0;
                    break;
            }
        } else {
            // 5-step: 7457, 14913, 22371, (29829 idle), 37281; no IRQ
            switch (this.frameCycle) {
                case 7457:
                    this.clockQuarterFrame();
                    break;
                case 14913:
                    this.clockQuarterFrame();
                    this.clockHalfFrame();
                    break;
                case 22371:
                    this.clockQuarterFrame();
                    break;
                case 37281:
                    this.clockQuarterFrame();
                    this.clockHalfFrame();
                    this.frameCycle = 0;
                    break;
            }
        }
    }

    private clockQuarterFrame(): void {
        this.pulse1.clockEnvelope();
        this.pulse2.clockEnvelope();
        this.triangle.clockLinearCounter();
        this.noise.clockEnvelope();
    }

    private clockHalfFrame(): void {
        this.pulse1.clockLengthAndSweep();
        this.pulse2.clockLengthAndSweep();
        this.triangle.clockLengthCounter();
        this.noise.clockLengthCounter();
    }

    private sampleCycle(): void {
        this.sampleAcc += 1;
        if (this.sampleAcc < CYCLES_PER_SAMPLE) return;
        this.sampleAcc -= CYCLES_PER_SAMPLE;

        const pulseSum = this.pulse1.output() + this.pulse2.output();
        const pulseOut = PULSE_MIX[pulseSum];
        const t = this.triangle.output();
        const n = this.noise.output();
        const d = this.dmc.output();
        let tndOut = 0;
        if (t !== 0 || n !== 0 || d !== 0) {
            tndOut = 159.79 / (100 + 1 / (t / 8227 + n / 12241 + d / 22638));
        }
        const sample = pulseOut + tndOut;

        if (this.sampleQueue.length >= Apu2A03.MAX_QUEUED_SAMPLES) {
            // Drop the oldest half in one operation (amortized O(1) per push).
            // Stale backlog is useless for real-time audio anyway.
            this.sampleQueue.splice(0, Apu2A03.MAX_QUEUED_SAMPLES / 2);
        }
        this.sampleQueue.push(sample);
    }

    private updateIrqLine(): void {
        const line = this.frameIrq || this.dmc.irqFlag;
        if (line !== this.irqLine) {
            this.irqLine = line;
            this.logger.log(2, () => `IRQ line ${line ? 'asserted' : 'cleared'}`);
            if (this.irqCallback) this.irqCallback(line);
        }
    }
}
